import _ from 'underscore';
import classNames from 'classnames';
import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { RenderLogger } from '@bingads-webui-react/fabric-with-signal';
import { StepsContext } from './steps-context';
import { pathPropType } from './path-prop-type';
/**
 * Represents a single step in the wizard, can contain either the UI for this step or a
 * <StepContainer /> element with nested Steps inside it.
 * @param {*} props - See propTypes definition.
 * @returns {ReactNode} Step UI.
 */
export function Step(props) {
  const {
    path,
    title,
    description,
    enabled,
    id,
    className,
    children,
    enableRenderingLog,
  } = props;

  const stepsContext = useContext(StepsContext);
  if (!_.isObject(stepsContext)) {
    throw new Error('StepsContext not found');
  }
  const { currentPath } = stepsContext;

  // generate a regular expression based on the path
  // regex will test if this step's path is part of the current path
  const isStepInPathRegex = useMemo(
    () => new RegExp(`^${path.replace(/\./g, '\\$&')}\\b`),
    [path]
  );

  if (!enabled || !isStepInPathRegex.test(currentPath)) {
    return null;
  }

  return (
    <div
      id={id}
      className={classNames('step', className)}
      data-step-path={path}
      data-step-title={title}
      data-step-desc={description}
    >
      { enableRenderingLog ? <RenderLogger title={path}> { children } </RenderLogger> : children }
    </div>
  );
}

Step.propTypes = {
  /**
   * Unique identifier for this step, can be hierarchical. E.g. 'my-step', or 'my-step.part1'.
   */
  path: pathPropType.isRequired,

  /**
   * Title controls the text displayed inside nav elements like <StepBreadcrumbs /> and
   * <StepProgressList />. If title is null, the step will not be displayed in these elements.
   */
  title: PropTypes.string,

  /**
   * Set enabled to false if you don't want the step to be visible
   */
  enabled: PropTypes.bool,
  description: PropTypes.string,

  id: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  enableRenderingLog: PropTypes.bool,
};

Step.defaultProps = {
  enabled: true,
  id: null,
  description: null,
  className: null,
  title: null,
  enableRenderingLog: false,
};
